/*
Copyright (c) 2026 Wind River Systems, Inc.

The right to copy, distribute, modify, or otherwise make use of this software may be licensed only pursuant to the terms of an applicable Wind River license agreement.
*/
import { Component, OnDestroy, OnInit } from '@angular/core';
// Services
import { AppCentralLayoutValue, EntitlementService, SettingsService, SettingsObj, ThemingService } from '@windriver/ui';
import { AuthService } from '@windriver/authentication';
// Models
import { App, AppList } from '@models';
import { Subscription } from 'rxjs';

@Component({
  selector: 'wsn-app-list',
  templateUrl: './app-list.component.html',
  styleUrls: ['./app-list.component.scss']
})
export class AppListComponent implements OnInit, OnDestroy {
  /** Flag to show/hide spinner. */
  public loading: boolean;
  public layout: string;
  public AppCentralLayoutValue = AppCentralLayoutValue;
  /** Flag to open the apps in a new tab or not. */
  public openInNewTab: string;
  /** Error message to display in case loading apps list fails. */
  public errorMessage: string;
  /** parameter to store the Observable */
  private subscriptions: Subscription;

  /** App list to display in view. */
  appList: AppList;
  staticApp: App[] = [
    {
      id: '',
      name: 'FOSSology',
      shortName: 'FOSSology',
      url: 'https://www.fossology.org/about/contact',
      isThirdParty: true,
      isFeature: false,
      iconName: 'fossology.png'
    },
    {
      id: '',
      name: 'LDRA',
      shortName: 'LDRA',
      url: 'https://ldra.com/contact-us/',
      isThirdParty: true,
      isFeature: false,
      iconName: 'ldra.png'
    },
    {
      id: '',
      name: 'Coverity',
      shortName: 'Synopsys',
      url: 'https://www.synopsys.com/software-integrity/security-testing/static-analysis-sast.html?utm_source=bing&utm_medium=cpc&utm_term=coverity_synopsys&utm_campaign=B_S_Coverity_Exact&cmp=ps-SIG-B_S_Coverity_Exact&msclkid=472545725dee1308db1e25b59cf3318f',
      isThirdParty: true,
      isFeature: false,
      iconName: 'synopsys-light.svg'
    },
    {
      id: '',
      name: 'Flexnet Code Insight',
      shortName: 'Revenera',
      url: 'https://www.revenera.com/software-composition-analysis/products/flexnet-code-insight.html',
      isThirdParty: true,
      isFeature: false,
      iconName: 'revenera-light.svg'
    }
  ];

  constructor(
    private authService: AuthService,
    private settingService: SettingsService,
    private entitlementService: EntitlementService,
    private themeService: ThemingService
  ) {
    this.layout = null;
    this.openInNewTab = null;
    this.loading = true;
    this.subscriptions = new Subscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.add(
      this.entitlementService.entitlementsRefreshSubject.subscribe(() => {
        this.getApps();
      })
    );
    this.subscriptions.add(
      this.themeService.colorSchemeSubject.subscribe((color: string) => {
        if (color === 'dark') {
          this.staticApp[2].iconName = 'synopsys-dark.svg';
          this.staticApp[3].iconName = 'revenera-dark.svg';
        } else {
          this.staticApp[2].iconName = 'synopsys-light.svg';
          this.staticApp[3].iconName = 'revenera-light.svg';
        }
      })
    );
    this.subscriptions.add(
      this.settingService.settingState.subscribe((settings: SettingsObj) => {
        this.layout = settings.appCentralLayout;
        this.openInNewTab = settings.openingApps;
      })
    );
  }

  async getApps(): Promise<void> {
    try {
      const result = await this.authService.getAllApps().toPromise();
      if (result) {
        if (!result.data || result.data.thirdPartyApps.length === 0) {
          this.errorMessage = 'Sorry, we tried but there was an error when loading the list of applications.';
        } else {
          this.appList = result.data;
        }
      }
      this.loading = false;
    } catch (error) {
      console.warn(error);
      this.loading = false;
    }
  }

  // checks if an app is in the favorite apps list
  isFavorite(id: string): boolean {
    return this.appList.favoriteApps.some((app) => app.id === id);
  }
}
